import { useEffect, useState } from "react";
import { FormContext, FormItem, FormItemOption } from "../form-builder.model";
import { FormWidgetService } from "../widgets/form-widget-service";
import {
  ValidationIssue,
  FormValidator,
  hasBlockingIssue,
  issuesForField,
} from "../validation";
import "./form-renderer.scss";

/**
 * A section of a form: its fields under a title, and a note shown above them, such as why a plugin's
 * configuration has no defaults (its completion failed).
 */
export type FormSection = { title: string; items: FormItem[]; note?: { severity: "error" | "info"; message: string } };

/**
 * What a form action reports back; shown inside the form. `values` are merged into the form's values
 * (Discover filling the seeds); `issues` show under the field each names (Validate's messages), until
 * the next edit makes them stale. Neither saves anything.
 */
export type FormActionResult = {
  severity: "success" | "error" | "info";
  message: string;
  values?: Record<string, unknown>;
  issues?: ValidationIssue[];
};

/**
 * A button beside Save that runs on the form's CURRENT, unsaved values — e.g.
 * "Test connection" on a database form, which must test the password just
 * typed, not the stored one. It does not save; its result shows in the form.
 */
export type FormAction = {
  label: string;
  run: (values: Record<string, unknown>, context?: FormContext) => Promise<FormActionResult | void>;
};

export type FormRendererProps = {
  title?: string;
  /** grouped items (L2 sections). A single untitled section renders as a flat form. */
  sections: FormSection[];
  initialValues: Record<string, unknown>;
  /** whole-form validator: (values) -> issues. Runs on submit (+ on change once
   *  issues are showing, to clear them as the user fixes things). */
  validate?: FormValidator;
  /** ambient lineage context (parent-view selections) — handed to field
   *  validators and custom widgets/renders. */
  context?: FormContext;
  onSubmit: (values: Record<string, unknown>) => Promise<void> | void;
  onCancel?: () => void;
  submitLabel?: string;
  /** buttons beside Save that run on the unsaved values (see FormAction) */
  actions?: FormAction[];
};

type Vals = Record<string, unknown>;

/** Resolve select options that may be an array, a Promise, or a thunk. */
function SelectControl(props: {
  item: FormItem & { type: "select" | "combobox" };
  value: unknown;
  onChange: (v: string) => void;
  invalid?: boolean;
}) {
  const [opts, setOpts] = useState<FormItemOption[]>(
    Array.isArray(props.item.options) ? props.item.options : [],
  );
  useEffect(() => {
    const o = props.item.options;
    if (Array.isArray(o)) setOpts(o);
    else Promise.resolve(typeof o === "function" ? o() : o).then(setOpts);
  }, [props.item]);
  const cur = String(props.value ?? "");
  // A controlled <select> whose value matches no <option> silently DISPLAYS the
  // first option while state stays unchanged (no onChange fires) — so an empty
  // required field looks filled yet submits as "", and a single-option lookup
  // can never be picked. Show an explicit placeholder until the value matches a
  // real option, so every selection is a genuine change event.
  const matched = opts.some((o) => String(o.value) === cur);
  return (
    <select value={cur} onChange={(e) => props.onChange(e.target.value)}>
      {!matched && <option value="">— select —</option>}
      {opts.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.text}
        </option>
      ))}
    </select>
  );
}

function isMono(name: string) {
  return ["payload_json", "cwd", "schedule_expr", "command"].some((n) => name.includes(n));
}

export function FormRenderer(props: FormRendererProps) {
  const [values, setValues] = useState<Vals>(props.initialValues);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<FormActionResult | null>(null);
  // what an action said about the fields (Validate's messages): shown under each, apart from the
  // validators' issues, so they do not read as "couldn't save yet"; an edit makes them stale
  const [actionIssues, setActionIssues] = useState<ValidationIssue[]>([]);

  const runAction = async (a: FormAction) => {
    setRunning(a.label);
    setActionResult(null);
    try {
      const r = await a.run(values, props.context);
      if (r) {
        setActionResult(r);
        if (r.values) setValues((prev) => ({ ...prev, ...r.values }));
        setActionIssues(r.issues ?? []);
      }
    } catch (e) {
      setActionResult({ severity: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setRunning(null);
    }
  };

  const actionButtons = (props.actions ?? []).map((a) => (
    <button
      key={a.label}
      className="dfr-btn"
      onClick={() => void runAction(a)}
      disabled={saving || running !== null}
    >
      {running === a.label ? `${a.label}…` : a.label}
    </button>
  ));

  const allItems = props.sections.flatMap((s) => s.items);
  // Field validators (from each item) composed with the whole-form validator.
  const runValidation = (v: Vals): ValidationIssue[] => [
    ...(props.validate ? props.validate(v) : []),
    ...allItems.flatMap((it) =>
      it.validate ? it.validate(v[it.name], v, props.context) : [],
    ),
  ];

  const setField = (name: string, v: unknown) => {
    if (actionIssues.length) setActionIssues([]);
    setValues((prev) => {
      const next = { ...prev, [name]: v };
      // once issues are showing, re-validate the whole form live as things change
      if (issues.length) setIssues(runValidation(next));
      return next;
    });
  };

  // `hidden` = a carried value with no UI (e.g. a lineage-preset FK column);
  // it still submits. `showWhen` = conditional visibility whose hidden values
  // are EXCLUDED at submit (see dataset-form). Different semantics on purpose.
  const visible = (item: FormItem) =>
    !item.hidden && !(item.showWhen && !item.showWhen(values));

  const save = async () => {
    const found = runValidation(values);
    setIssues(found);
    if (hasBlockingIssue(found)) return;
    setSaving(true);
    try {
      await props.onSubmit(values);
    } finally {
      setSaving(false);
    }
  };

  const problemCount = issues.length;

  const control = (item: FormItem) => {
    const val = values[item.name];
    const set = (v: unknown) => setField(item.name, v);
    if (item.render)
      return item.render({ value: val, onChange: set, values, context: props.context });
    const mono = isMono(item.name) ? " mono" : "";
    switch (item.type) {
      case "toggle":
      case "checkbox":
        return (
          <button
            type="button"
            className="dfr-sw"
            role="switch"
            aria-checked={Boolean(val)}
            onClick={() => set(!val)}
          />
        );
      case "select":
      case "combobox":
        return <SelectControl item={item} value={val} onChange={set} />;
      case "number":
        return (
          <input
            type="number"
            value={val === undefined || val === null ? "" : String(val)}
            onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))}
          />
        );
      case "textarea":
      case "json": {
        const text =
          typeof val === "string" ? val : val == null ? "" : JSON.stringify(val, null, 2);
        return (
          <textarea
            className={mono.trim()}
            value={text}
            onChange={(e) => set(e.target.value)}
          />
        );
      }
      case "widget": {
        // Resolve through the pluggable widget registry (the same registry
        // the legacy form-input path uses), handing it the standard props +
        // the form's ambient context. An unknown widget name renders a
        // visible placeholder rather than silently dropping the field.
        const w = FormWidgetService.get().get(item.widget);
        if (!w) {
          return (
            <div className="dfr-help">
              [{item.widget}] — widget not registered
            </div>
          );
        }
        // Map this field's issues into the FormWidgetProps error shape.
        const fieldIssues = issuesForField([...issues, ...actionIssues], item.name);
        return w.render({
          formItem: item,
          formData: values,
          formErrors: {
            [item.name]: fieldIssues.map((iss) => ({
              valid: iss.severity !== "error",
              message: iss.message,
              severity: iss.severity === "warning" ? "warning" : iss.severity === "error" ? "error" : "info",
            })),
          },
          setValue: set,
          context: props.context,
        });
      }
      default:
        return (
          <input
            className={mono.trim()}
            type={item.type === "password" ? "password" : "text"}
            value={val == null ? "" : String(val)}
            onChange={(e) => set(e.target.value)}
          />
        );
    }
  };

  const field = (item: FormItem) => {
    if (!visible(item)) return null;
    const fieldIssues = issuesForField([...issues, ...actionIssues], item.name);
    const sev = fieldIssues[0]?.severity;
    const cls =
      "dfr-field" + (sev === "error" ? " err" : sev === "warning" ? " warn" : "");
    return (
      <div
        className={cls}
        key={item.name}
        onBlur={() => setIssues(runValidation(values))}
      >
        <label>
          {item.label ?? item.name}
          {item.required ? <span className="req">*</span> : null}
        </label>
        {control(item)}
        {item.helpText ? <div className="dfr-help">{item.helpText}</div> : null}
        {/* a list places its own messages, each under the row it is about */}
        {!(item.type === "widget" && (item.widget === "list" || item.widget === "records")) &&
          fieldIssues.map((iss, i) => (
            <div className={`dfr-field-msg ${iss.severity}`} key={i}>
              {iss.message}
            </div>
          ))}
      </div>
    );
  };

  const sectionErrorCount = (s: FormSection) =>
    s.items.reduce((n, it) => n + issuesForField(issues, it.name).filter((x) => x.severity === "error").length, 0);

  const multiSection = props.sections.length > 1;

  return (
    <div className="dfr">
      <div className="dfr-head">
        <div className="grow">
          <div className="dfr-eyebrow">Continuum · Form</div>
          <h1>{props.title ?? "Edit"}</h1>
        </div>
        <div className="dfr-btns">
          {actionButtons}
          {props.onCancel && (
            <button className="dfr-btn" onClick={props.onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button className="dfr-btn primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : props.submitLabel ?? "Save"}
          </button>
        </div>
      </div>

      {actionResult && (
        <div className={`dfr-action-result ${actionResult.severity}`} role="status" data-testid="dfr-action-result">
          {actionResult.message}
        </div>
      )}

      {issues.length > 0 && (
        <div className="dfr-banner" role="alert">
          <div className="dfr-banner-icon">!</div>
          <div>
            <div className="dfr-banner-title">
              Couldn't save yet — {problemCount} {problemCount === 1 ? "problem" : "problems"} to fix
            </div>
            <ul>
              {issues.map((iss, i) => (
                <li key={i}>{iss.message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="dfr-panel">
        {props.sections.map((s, si) => (
          <section className="dfr-sec" key={si}>
            {multiSection && (
              <div className="dfr-sec-head">
                <h2>{s.title}</h2>
                {sectionErrorCount(s) > 0 && (
                  <span className="count">{sectionErrorCount(s)}</span>
                )}
              </div>
            )}
            {s.note && (
              <div className={`dfr-action-result ${s.note.severity}`} role="note" data-testid="dfr-section-note">
                {s.note.message}
              </div>
            )}
            <div className="dfr-grid">{s.items.map(field)}</div>
          </section>
        ))}

        <div className="dfr-foot">
          <span className="note">
            Validation runs on save — server rules surface on the offending field.
          </span>
          <span className="spacer" />
          {actionButtons}
          {props.onCancel && (
            <button className="dfr-btn" onClick={props.onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button className="dfr-btn primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : props.submitLabel ?? "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
